import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { z } from "zod";
import { invitationSchema } from "./useInvitation";
import { courseProgressSchema } from "./useCourses";
import { useAuth } from "@clerk/clerk-react";

// Merge only defined values into cached data. Prevents partial API responses
// (bare Prisma users without relations) from overwriting cached fields with undefined.
function safeMerge<T extends Record<string, any>>(old: T, patch: Record<string, any>): T {
  const cleaned = Object.fromEntries(Object.entries(patch).filter(([, v]) => v !== undefined));
  return { ...old, ...cleaned } as T;
}

const userRoleSchema = z.enum(["SUPERADMIN", "ADMIN", "USER"]);
export const licenseSchema = z.enum(["FREE", "PAID"]);

// New enums for onboarding
export const onboardingStepSchema = z.enum([
  "NAME",
  "AGE",
  "CONTENT_PREFERENCE",
  "WELCOME_KITS",
  "LICENSES_INVITATIONS",
  "COMPLETE",
]);
export type OnboardingStep = z.infer<typeof onboardingStepSchema>;

export const contentPreferenceSchema = z.enum([
  "READ",
  "WATCH",
  "LISTEN",
  "NO_PREFERENCE",
]);
export type ContentPreference = z.infer<typeof contentPreferenceSchema>;

export const ageRangeSchema = z.enum([
  "UNDER_50",
  "BETWEEN_50_AND_60",
  "BETWEEN_60_AND_70",
  "BETWEEN_70_AND_80",
  "OVER_80",
]);
export type AgeRange = z.infer<typeof ageRangeSchema>;

export const userSchema = z.object({
  id: z.string(),
  clerkId: z.string(),
  name: z.string().optional().nullable(),
  ageRange: ageRangeSchema.optional().nullable(),
  contentPreference: contentPreferenceSchema.optional().nullable(),
  onboardingStep: onboardingStepSchema.optional().nullable(),
  market: z.enum(["GB", "US", "CA", "AU"]).optional().nullable(),
  role: userRoleSchema,
  stripeCustomerId: z.string().nullable(),
  subscriptions: z.array(z.any()).optional(),
  license: licenseSchema,
  parentUserId: z.string().nullable(),
  childUsers: z.array(z.any()).optional(),
  lessonProgress: z.array(z.any()).optional(),
  articleProgress: z.array(z.any()).optional(),
  courseProgress: z.array(courseProgressSchema).optional().default([]),
  behaviourScore: z.array(z.any()).optional(),
  invitations: z.array(invitationSchema).optional(),
  email: z.string().optional(),
  dataBreachRetrievalDate: z.string().nullable().optional(),
  exp: z.number().optional(),
  archived: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
  dashboardSeenAt: z.string().nullable().optional(),
  onboardingProgress: z
    .object({
      requestKit: z.boolean(),
      lookAround: z.boolean(),
      startLesson: z.boolean(),
    })
    .optional()
    .nullable(),
  isTestAccount: z.boolean().optional(),
  optIntoMarketing: z.boolean().optional(),
  optIntoNewsletter: z.boolean().optional(),
  progressVisible: z.boolean().optional(),
  avatarConfig: z.any().optional().nullable(),
});

export type User = z.infer<typeof userSchema>;
export type UserRole = z.infer<typeof userRoleSchema>;
export type LicenseType = z.infer<typeof licenseSchema>;

export enum UserExpActionType {
  ARTICLE = "ARTICLE",
  LESSON = "LESSON",
}

// For some reason, I can't see the zod errors unless I catch them here

export const getUserFromClerkId = (clerkId: string, options = {}) => {
  const { getToken } = useAuth();
  return useQuery({
    queryKey: ["user", clerkId],
    queryFn: async () => {
      try {
        const token = await getToken();
        const response = await axios.get(`${import.meta.env.VITE_API_URL}/app/users/clerk/${clerkId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        return userSchema.parse(response.data);
      } catch (error) {
        if (error instanceof z.ZodError) {
          console.error("Zod validation error:", error.errors);
        }
        throw error;
      }
    },
    ...options,
  });
};

export const getParentUser = (options = {}) => {
  const { getToken } = useAuth();
  return useQuery({
    queryKey: ["user", "parent"],
    queryFn: async () => {
      const token = await getToken();
      const response = await axios.get(`${import.meta.env.VITE_API_URL}/app/users/parent`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return userSchema.parse(response.data);
    },
    ...options,
  });
};

export const createUserFromClerkId = () => {
  const queryClient = useQueryClient();
  const { getToken } = useAuth();

  return useMutation({
    mutationFn: async ({ clerkId, market }: { clerkId: string; market?: string }) => {
      const token = await getToken();
      const headers = token
        ? { Authorization: `Bearer ${token}` }
        : undefined;
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/public/register/${clerkId}`,
        { market },
        headers ? { headers } : undefined
      );
      const createdUser = userSchema.parse(response.data);
      return createdUser;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
};

// Stamps the first dashboard visit server-side so the greeting can show
// "Welcome aboard" once and "Welcome back" thereafter. Intentionally does NOT
// invalidate the user query: the dashboard freezes its first-visit decision on
// load, and a refetch would flip the greeting mid-session.
export const useMarkDashboardSeen = () => {
  const { getToken } = useAuth();

  return useMutation({
    mutationFn: async () => {
      const token = await getToken();
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/app/users/dashboard-seen`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data as { firstVisit: boolean };
    },
  });
};

// Onboarding completion must never hang. Clerk's getToken() and an axios
// request without a timeout can both stay pending indefinitely (TD-037), which
// left the final onboarding button stuck on "Loading...". Bound both.
const ONBOARDING_REQUEST_TIMEOUT_MS = 20_000;

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms);
    promise.then(
      (value) => { clearTimeout(timer); resolve(value); },
      (error) => { clearTimeout(timer); reject(error); },
    );
  });
}

async function getTokenOrThrow(getToken: () => Promise<string | null>): Promise<string> {
  const token = await withTimeout(getToken(), 10_000, "Clerk getToken");
  if (!token) throw new Error("No session token available");
  return token;
}

// LEGACY: This is currently used for the 'initial steps' cards.
export const updateOnboardingProgress = () => {
  const queryClient = useQueryClient();
  const { getToken } = useAuth();

  return useMutation({
    mutationFn: async (onboardingProgress: { requestKit: boolean; lookAround: boolean; startLesson: boolean }) => {
      const token = await getToken();
      const response = await axios.put(`${import.meta.env.VITE_API_URL}/app/users/onboarding`, { onboardingProgress }, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return userSchema.parse(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
};

// CURRENT: This is used for the onboarding form after login.
export const updateOnboardingStep = () => {
  const queryClient = useQueryClient();
  const { getToken } = useAuth();
  
  return useMutation({
    // 'always': never silently pause while the browser reports offline; let the
    // request fail (and the UI recover) instead.
    networkMode: "always",
    mutationFn: async (onboardingStep: OnboardingStep) => {
      const token = await getTokenOrThrow(getToken);
      const response = await axios.put(
        `${import.meta.env.VITE_API_URL}/app/users/onboarding-step`,
        { onboardingStep },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          timeout: ONBOARDING_REQUEST_TIMEOUT_MS,
        }
      );
      return userSchema.parse(response.data);
    },
    onSuccess: (_data, step) => {
      import('@/lib/posthog').then(({ trackEvent }) => {
        trackEvent('onboarding_step_completed', { step });
      });
      queryClient.invalidateQueries({ queryKey: ["users"] });
      queryClient.invalidateQueries({ queryKey: ["user", "onboardingStep"] });
    },
  });
};

export const updateOnboardingInfo = () => {
  const queryClient = useQueryClient();
  const { getToken } = useAuth();

  return useMutation({
    networkMode: "always",
    mutationFn: async (info: Partial<{ name: string; ageRange: string; contentPreference: string }>) => {
      const token = await getTokenOrThrow(getToken);
      const response = await axios.put(`${import.meta.env.VITE_API_URL}/app/users/onboarding-info`, info, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        timeout: ONBOARDING_REQUEST_TIMEOUT_MS,
      });
      return userSchema.parse(response.data);
    },
    onSuccess: (data) => {
      if (data?.clerkId) {
        queryClient.setQueryData(["user", data.clerkId], (old: any) => old ? safeMerge(old, data) : old);
        queryClient.invalidateQueries({ queryKey: ["user", data.clerkId] });
      }
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
};

export const getOnboardingStep = () => {
  const { getToken } = useAuth();
  return useQuery({
    queryKey: ["user", "onboardingStep"],
    queryFn: async () => {
      const token = await getToken();
      const response = await axios.get(`${import.meta.env.VITE_API_URL}/app/users/onboarding-step`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return onboardingStepSchema.parse(response.data);
    },
  });
};

// Add this schema for onboarding info only
export const onboardingInfoSchema = z.object({
  name: z.string().optional().nullable(),
  ageRange: ageRangeSchema.optional().nullable(),
  contentPreference: contentPreferenceSchema.optional().nullable(),
  pendingWelcomeKitsCount: z.number().optional().nullable(),
});

export const getOnboardingInfo = () => {
  const { getToken } = useAuth();
  return useQuery({
    queryKey: ["user", "onboardingInfo"],
    queryFn: async () => {
      const token = await getToken();
      const response = await axios.get(`${import.meta.env.VITE_API_URL}/app/users/onboarding-info`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return onboardingInfoSchema.parse(response.data);
    },
  });
};

export type FamilyMemberInvite = {
  email: string;
  name?: string;
  relationship?: string;
  techComfort?: string;
  focusAreas?: string;
  personalMessage?: string;
};

export const inviteChildUser = () => {
  const queryClient = useQueryClient();
  const { getToken } = useAuth();

  return useMutation({
    mutationFn: async (data: FamilyMemberInvite) => {
      const token = await getToken();
      const response = await axios.post(`${import.meta.env.VITE_API_URL}/app/users/child/invite`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return invitationSchema.parse(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user"] });
      queryClient.invalidateQueries({ queryKey: ["users"] });
      queryClient.invalidateQueries({ queryKey: ["family"] });
    },
  });
};

export const removeChildUser = () => {
  const queryClient = useQueryClient();
  const { getToken } = useAuth();

  return useMutation({
    mutationFn: async ({ childUserId, keepLicenseSlot = true }: { childUserId: string; keepLicenseSlot?: boolean }) => {
      const token = await getToken();
      const response = await axios.delete(
        `${import.meta.env.VITE_API_URL}/app/users/child/${childUserId}?keepLicenseSlot=${keepLicenseSlot}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          timeout: 30000,
        }
      );
      return userSchema.parse(response.data);
    },
    onMutate: async ({ childUserId }) => {
      await queryClient.cancelQueries({ queryKey: ["user", childUserId] });
      const previousUser = queryClient.getQueryData<User>(["user", childUserId]);
      if (previousUser) {
        queryClient.setQueryData(["user", childUserId], {
          ...previousUser,
          childUsers: (previousUser.childUsers || []).filter(child => child.id !== childUserId),
        });
      }
      return previousUser;
    },
    onSuccess: (_, __, context) => {
      if (context?.clerkId) {
        queryClient.invalidateQueries({ queryKey: ["user", context.clerkId] });
      }
      queryClient.invalidateQueries({ queryKey: ["user"] });
      queryClient.invalidateQueries({ queryKey: ["users"] });
      queryClient.invalidateQueries({ queryKey: ["family"] });
    },
  });
};

export const deleteUser = () => {
  const queryClient = useQueryClient();
  const { getToken } = useAuth();

  return useMutation({
    mutationFn: async () => {
      const token = await getToken();
      const response = await axios.delete(`${import.meta.env.VITE_API_URL}/app/users/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return userSchema.parse(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
};

export const getUserLevel = () => {
  const { getToken } = useAuth();
  return useQuery({
    queryKey: ["user", "level"],
    queryFn: async () => {
      const token = await getToken();
      const response = await axios.get(`${import.meta.env.VITE_API_URL}/app/users/level`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return z.number().parse(response.data);
    },
  });
};

export const updateUserAvatar = () => {
  const queryClient = useQueryClient();
  const { getToken } = useAuth();

  return useMutation({
    mutationFn: async (avatarConfig: Record<string, any> | null) => {
      const token = await getToken();
      await axios.put(
        `${import.meta.env.VITE_API_URL}/app/users/avatar`,
        { avatarConfig },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user"] });
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
};

export const updateUserOptIn = () => {
  const queryClient = useQueryClient();
  const { getToken } = useAuth();

  return useMutation({
    mutationFn: async ({ optIntoMarketing = false, optIntoNewsletter = false }: { optIntoMarketing?: boolean, optIntoNewsletter?: boolean }) => {
      const token = await getToken();
      const response = await axios.put(
        `${import.meta.env.VITE_API_URL}/app/users/opt-in`,
        {
          optIntoMarketing,
          optIntoNewsletter,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return userSchema.parse(response.data);
    },
    onSuccess: (data) => {
      if (data?.clerkId) {
        queryClient.setQueryData(["user", data.clerkId], (old: any) => old ? safeMerge(old, data) : old);
        queryClient.invalidateQueries({ queryKey: ["user", data.clerkId] });
      }
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
};

export const updateUserMarket = () => {
  const queryClient = useQueryClient();
  const { getToken } = useAuth();

  return useMutation({
    mutationFn: async ({ market }: { market: "GB" | "US" | "CA" | "AU" }) => {
      const token = await getToken();
      const response = await axios.put(
        `${import.meta.env.VITE_API_URL}/app/users/market`,
        { market },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return userSchema.parse(response.data);
    },
    onSuccess: (data) => {
      if (data?.clerkId) {
        queryClient.setQueryData(["user", data.clerkId], (old: any) => old ? safeMerge(old, data) : old);
        queryClient.invalidateQueries({ queryKey: ["user", data.clerkId] });
      }
      queryClient.invalidateQueries({ queryKey: ["users"] });
      queryClient.invalidateQueries({ queryKey: ["articles"] });
      queryClient.invalidateQueries({ queryKey: ["scams"] });
      queryClient.invalidateQueries({ queryKey: ["dailyTip"] });
      queryClient.invalidateQueries({ queryKey: ["scamOfTheDay"] });
    },
  });
};

const guidedTourCompleteSchema = z.object({
  awardedPoints: z.number(),
  alreadyAwarded: z.boolean(),
});

export const completeGuidedTourReward = () => {
  const queryClient = useQueryClient();
  const { getToken } = useAuth();

  return useMutation({
    mutationFn: async () => {
      const token = await getToken();
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/app/users/guided-tour/complete`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return guidedTourCompleteSchema.parse(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
      queryClient.invalidateQueries({ queryKey: ["mixedEvents"] });
      queryClient.invalidateQueries({ queryKey: ["missions"] });
      queryClient.invalidateQueries({ queryKey: ["user", "level"] });
      queryClient.invalidateQueries({ queryKey: ["user"] });
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
};

export const completeBreachExplainer = () => {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const token = await getToken();
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/app/users/breach-explainer/complete`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return guidedTourCompleteSchema.parse(response.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
      queryClient.invalidateQueries({ queryKey: ["mixedEvents"] });
      queryClient.invalidateQueries({ queryKey: ["user", "level"] });
      queryClient.invalidateQueries({ queryKey: ["user"] });
    },
  });
};